import { useCallback, useMemo } from 'react';
import { useAuth } from 'src/auth';
import { getValidatedFromLocalStorage, setJSONInLocalStorage } from 'src/utils/safeStorage';
import { z } from 'zod';

/** A saved layout row, or the built-in layout, which has no row (and so no id). */
export type RememberedTagLayout = { id: number } | { builtIn: true };

const rememberedTagLayoutSchema = z.union([
  z.object({ id: z.number().int().positive() }),
  z.object({ builtIn: z.literal(true) }),
]);

/**
 * The tag layout this user last created pallet tags with, per organization on this browser.
 * Without it the drawer opens on the first layout of the list (oldest first), which is rarely
 * the one a packer prints with (TAS-3660: "it always reverts back to PTI").
 */
export function useLastTagLayout(): {
  lastTagLayout: RememberedTagLayout | null;
  rememberTagLayout: (id: number | null | undefined) => void;
} {
  const { currentUser } = useAuth();
  const storageKey = useMemo(
    () => (currentUser ? `palletTags.lastTagLayout:${currentUser.id}:${currentUser.organizationId ?? ''}` : null),
    [currentUser]
  );
  const lastTagLayout = useMemo(
    () => (storageKey ? (getValidatedFromLocalStorage(storageKey, rememberedTagLayoutSchema) ?? null) : null),
    [storageKey]
  );
  const rememberTagLayout = useCallback(
    (id: number | null | undefined) => {
      if (storageKey) setJSONInLocalStorage(storageKey, id ? { id } : { builtIn: true });
    },
    [storageKey]
  );

  return { lastTagLayout, rememberTagLayout };
}

/** Layout precedence: this browser's remembered layout (a listed row, else the built-in `fallback`), then the organization's `isLastUsed` row, then `fallback` when every row says false, then the list's first when there is no history at all (`isLastUsed` null). */
export function initialTagLayout<T extends { id?: number | null; isLastUsed?: boolean | null }>(
  tagLayouts: readonly T[] | undefined,
  lastTagLayout: RememberedTagLayout | null,
  fallback: T
): T {
  if (lastTagLayout && 'builtIn' in lastTagLayout) return fallback;
  const remembered = lastTagLayout && tagLayouts?.find((layout) => layout.id === lastTagLayout.id);
  if (remembered) return remembered;

  const lastUsed = tagLayouts?.find((layout) => layout.isLastUsed);
  if (lastUsed) return lastUsed;
  if (tagLayouts?.some((layout) => layout.isLastUsed === false)) return fallback;

  return tagLayouts?.[0] ?? fallback;
}
